import { useCallback, useContext } from 'react';
import type { IFieldElem } from '@models/IFormData';
import {
    type FormulaTraceStatus,
    type IFormulaTraceTarget,
    DocumentFormulaTraceParentContext,
    useDocumentFormulaTraceRegistry,
} from '@/context/DocumentFormulaTraceContext';

interface IFormulaDependencySource {
    GetFields(): string[];
    GetPreparedExpression?(): string | undefined;
}

export const useFormulaTracePublisher = (
    source: string | undefined,
    fields: Record<string, IFieldElem>,
    dependencySource: IFormulaDependencySource | null | undefined,
    externalSources: string[],
    requestedTarget?: IFormulaTraceTarget,
) => {
    const registry = useDocumentFormulaTraceRegistry();
    const traceParent = useContext(DocumentFormulaTraceParentContext);
    const targetId = requestedTarget?.targetId;
    const targetKind = requestedTarget?.targetKind;
    const targetName = requestedTarget?.targetName;
    const ruleKind = requestedTarget?.ruleKind;

    const publishTrace = useCallback(
        (result: unknown, status: FormulaTraceStatus, error?: string, watchedValues?: unknown[]) => {
            if (!registry || !targetId || !targetKind || !ruleKind || !source) return;

            const dependencyFields = dependencySource?.GetFields() ?? [];
            registry.upsert({
                targetId,
                targetKind,
                targetName,
                ruleKind,
                parentTargetId: requestedTarget?.parentTargetId ?? traceParent?.targetId,
                parentTargetKind: requestedTarget?.parentTargetKind ?? traceParent?.targetKind,
                source,
                preparedExpression: dependencySource?.GetPreparedExpression?.(),
                result,
                dependencies: dependencyFields.map((field, index) => ({
                    field,
                    value: watchedValues?.[index] ?? fields[field]?.value,
                })),
                externalSources,
                status,
                error,
            });
        },
        [
            dependencySource,
            externalSources,
            fields,
            registry,
            requestedTarget?.parentTargetId,
            requestedTarget?.parentTargetKind,
            ruleKind,
            source,
            targetId,
            targetKind,
            targetName,
            traceParent?.targetId,
            traceParent?.targetKind,
        ],
    );

    const removeTrace = useCallback(() => {
        if (!registry || !targetId || !targetKind || !ruleKind) return;
        registry.remove({ targetId, targetKind, targetName, ruleKind });
    }, [registry, ruleKind, targetId, targetKind, targetName]);

    return { publishTrace, removeTrace };
};
